import { IntlFormatters } from 'react-intl';
import { formTexts } from 'i18n';
import {
  CycleFormFields,
  TemplateFormFields,
} from 'pages/ReviewCycles/interfaces';
import { CycleTemplate, CycleTemplateDTO } from 'store/reviewCycles/interfaces';
import {
  ALLOW_SCORECARD_OWNER_EDITS_PARAMETER_TYPE,
  CALCULATE_SELF_ASSESSMENT_PARAMETER_TYPE,
  ALLOW_SCORECARD_COMMENT_PARAMETER_TYPE,
  ENABLE_SELF_ASSESSMENT_PARAMETER_TYPE,
  ALLOW_SCORECARD_METRIC_PM_OWNER_EDITS_PARAMETER_TYPE,
  SCORECARD_GOAL_TYPE_METRIC,
  AUTO_ASSIGN_PM_REVIEWER_PARAMETER_TYPE,
  LIMIT_REVIEWERS_TO_EMPLOYEE_AND_PM_PARAMETER_TYPE,
  ALLOW_SCORECARD_OWNER_EDIT_ACTUAL_FIELD_PARAMETER_TYPE,
} from 'constants/scorecards';
import {
  SelfAssessmentSetups,
  isCuSatGoal,
  metricSubgoalDefaults,
  parseParams,
} from 'helpers';
import { ReviewCycleEventType } from 'constants/reviewCycleEvents';
import { ReviewCycleEvents } from 'types/reviewCycle';

function normalizeToUTCMidnight(
  dateValue: string | Date | null | undefined,
): string | null {
  if (!dateValue) return null;
  const localDate = new Date(dateValue);
  if (Number.isNaN(localDate.getTime())) return null;
  return new Date(
    Date.UTC(
      localDate.getFullYear(),
      localDate.getMonth(),
      localDate.getDate(),
      0,
      0,
      0,
      0,
    ),
  ).toISOString();
}

const weightRegex = /^\d{1,2}%?$/;

export const validateWeight = (
  value: string | number,
  formatMessage: IntlFormatters['formatMessage'],
) => {
  if (typeof value === 'string') {
    const trimmed = (value || '').trim();
    if (!trimmed) {
      return formatMessage(formTexts.errorRequired);
    }
    if (trimmed !== '100%' && trimmed !== '100' && !weightRegex.test(trimmed)) {
      return formatMessage(formTexts.errorPattern);
    }
  } else if (Number.isNaN(value) || value > 100 || value < 0) {
    return formatMessage(formTexts.errorPattern);
  }

  return true;
};

export const validateTotalWeight = (value: string | number | undefined) => {
  const parsedValue = value ? parseInt(value as string, 10) || 0 : 0;
  return parsedValue !== 100 ? 'error' : true;
};

const createEmptyEventDates = () => ({
  reminder: [],
  deadline: null,
});

const createEmptyEvents = (): ReviewCycleEvents =>
  Object.values(ReviewCycleEventType).reduce(
    (acc, eventType) => ({
      ...acc,
      [eventType]: createEmptyEventDates(),
    }),
    {} as ReviewCycleEvents,
  );

export const defaultCycleValues: CycleFormFields = {
  name: '',
  endAt: null,
  startAt: null,
  templateIds: [],
  cycleId: '',
  events: createEmptyEvents(),
};

export function formatCycleData(data: CycleFormFields, isEdit: boolean) {
  const formatEvents = (events: ReviewCycleEvents) =>
    Object.entries(events).reduce(
      (acc, [eventType, eventData]) => ({
        ...acc,
        [eventType]: {
          deadline: normalizeToUTCMidnight(eventData.deadline),
          reminder: eventData.reminder
            .filter(Boolean)
            .map(normalizeToUTCMidnight)
            .filter(Boolean),
        },
      }),
      {} as ReviewCycleEvents,
    );

  if (isEdit) {
    const { id, name, startAt, endAt, events } = data;
    return {
      id,
      name,
      startAt: normalizeToUTCMidnight(startAt),
      endAt: normalizeToUTCMidnight(endAt),
      events: formatEvents(events),
    };
  }

  const { cycleId, ...result } = data;
  return {
    ...result,
    startAt: normalizeToUTCMidnight(result.startAt),
    endAt: normalizeToUTCMidnight(result.endAt),
    events: formatEvents(result.events),
  };
}

export function formatGoalsData(
  goals: TemplateFormFields['goals'],
  isCommonGoals: boolean,
) {
  return goals.map((goal, goalIndex) => {
    const isCuSat = isCuSatGoal(goal);
    const { weight: goalWeight } = goal;
    if (isCuSat) {
      const { id, fromGoalTemplateId } = goal;
      return {
        orderRank: goalIndex + 1,
        weight:
          typeof goalWeight === 'string'
            ? parseInt(goalWeight, 10)
            : goalWeight,
        id,
        fromGoalTemplateId,
      };
    }
    const {
      name,
      description,
      subgoalsWeight,
      subgoals,
      type,
      allowScorecardOwnerEdits,
      allowScorecardComment,
      selfAssessmentSetup,
      allowScorecardMetricPmOwnerEdits,
      autoAssignPmReviewer,
      limitReviewersToEmployeeAndPm,
      allowScorecardOwnerEditActualField,
      ...goalFields
    } = goal;

    const isMetricGoal = type === SCORECARD_GOAL_TYPE_METRIC;

    return {
      ...goalFields,
      name: name?.trim(),
      description: description?.trim(),
      type,
      weight: isCommonGoals
        ? undefined
        : Number.parseInt(goalWeight as string, 10),
      orderRank: goalIndex + 1,
      params: [
        {
          alias: ALLOW_SCORECARD_OWNER_EDITS_PARAMETER_TYPE,
          value: isMetricGoal ? false : allowScorecardOwnerEdits,
        },
        {
          alias: CALCULATE_SELF_ASSESSMENT_PARAMETER_TYPE,
          value: isMetricGoal
            ? false
            : selfAssessmentSetup === SelfAssessmentSetups.calculate,
        },
        {
          alias: ALLOW_SCORECARD_COMMENT_PARAMETER_TYPE,
          value: isMetricGoal ? false : allowScorecardComment,
        },
        {
          alias: ENABLE_SELF_ASSESSMENT_PARAMETER_TYPE,
          value: isMetricGoal
            ? false
            : selfAssessmentSetup === SelfAssessmentSetups.request ||
              selfAssessmentSetup === SelfAssessmentSetups.calculate,
        },
        {
          alias: ALLOW_SCORECARD_METRIC_PM_OWNER_EDITS_PARAMETER_TYPE,
          value: allowScorecardMetricPmOwnerEdits,
        },
        {
          alias: ALLOW_SCORECARD_OWNER_EDIT_ACTUAL_FIELD_PARAMETER_TYPE,
          value: allowScorecardOwnerEditActualField,
        },
        {
          alias: AUTO_ASSIGN_PM_REVIEWER_PARAMETER_TYPE,
          value: autoAssignPmReviewer,
        },
        {
          alias: LIMIT_REVIEWERS_TO_EMPLOYEE_AND_PM_PARAMETER_TYPE,
          value: limitReviewersToEmployeeAndPm,
        },
      ],
      subgoals: subgoals.map(
        (
          {
            params,
            type: subGoalType,
            weight: subgoalWeight = 0,
            name: subgoalName,
            description: subgoalDescription,
            metricTarget,
            metricFormat,
            metricPerformanceFactor,
            weightTouched,
            ...subgoalFields
          },
          subgoalIndex,
        ) => ({
          ...subgoalFields,
          assessmentScaleGroup: undefined,
          name: subgoalName?.trim(),
          description: subgoalDescription?.trim(),
          orderRank: subgoalIndex + 1,
          weight: Number.parseInt(subgoalWeight as string, 10),
          ...(isMetricGoal &&
            metricTarget && {
              metricTarget,
            }),
          ...(isMetricGoal &&
            metricPerformanceFactor && {
              metricPerformanceFactor,
            }),
          ...(isMetricGoal &&
            metricFormat && {
              metricFormat,
            }),
        }),
      ),
    };
  }) as CycleTemplateDTO['goals'];
}

export function formatTemplateData({
  name: templateName,
  goals,
  totalWeight,
  entity,
  ...templateFields
}: TemplateFormFields): CycleTemplateDTO {
  return {
    ...templateFields,
    name: templateName?.trim(),
    goals: formatGoalsData(goals, false),
  };
}

export function parseTemplateData({
  countries,
  unit,
  goals,
  params: scorecardParams,
  ...templateData
}: CycleTemplate): TemplateFormFields {
  return {
    ...templateData,
    countryIds: countries.map(({ id }) => id),
    unitId: unit.id,
    goals: goals.map(({ subgoals, description, params, ...goalFields }) => {
      const {
        allowScorecardOwnerEdits,
        allowScorecardComment,
        selfAssessmentSetup,
        allowScorecardMetricPmOwnerEdits,
        allowScorecardOwnerEditActualField,
        autoAssignPmReviewer,
        limitReviewersToEmployeeAndPm,
      } = parseParams(params);
      return {
        ...goalFields,
        allowScorecardOwnerEdits: allowScorecardOwnerEdits ?? false,
        allowScorecardComment: allowScorecardComment ?? false,
        allowScorecardMetricPmOwnerEdits:
          allowScorecardMetricPmOwnerEdits ?? false,
        allowScorecardOwnerEditActualField:
          allowScorecardOwnerEditActualField ?? false,
        selfAssessmentSetup,
        autoAssignPmReviewer: autoAssignPmReviewer ?? false,
        limitReviewersToEmployeeAndPm: limitReviewersToEmployeeAndPm ?? false,
        description: description ?? undefined,
        subgoals: subgoals.map(
          ({
            assessmentScaleGroup,
            params: subgoalParams,
            ...subgoalFields
          }) => ({
            ...subgoalFields,
            assessmentScaleGroupId: assessmentScaleGroup?.id || '',
            ...metricSubgoalDefaults(subgoalFields),
          }),
        ),
      };
    }),
  };
}
